import { C, H2, H3, Lead, Note, OL, P, Pre, Table, Terms, UL } from '../Doc';

export function Developer() {
  return (
    <>
      <Lead>
        Integrating FormForge with your own systems: authenticating, driving forms from your
        backend, reading responses, receiving webhooks, and talking to the runtime directly.
        If you only need a form on a page, the <strong>Setup guide</strong> is shorter and
        probably enough.
      </Lead>

      <H2 id="choosing">Four ways in</H2>
      <Table
        head={['Approach', 'Good for', 'Cost']}
        rows={[
          ['Embed loader', 'A form inside your product, themed and sized automatically', 'A script tag'],
          ['Hosted link', 'Email, print, anywhere you cannot run script', 'Nothing'],
          ['REST API', 'Creating forms programmatically, syncing responses into your systems', 'Server-side work'],
          ['Runtime protocol', 'Your own interface around the real form engine', 'Most work, most control'],
        ]}
      />

      <H2 id="auth">Authentication</H2>
      <P>
        Server-to-server integration uses an API key. Create one under{' '}
        <strong>API keys</strong>; it is shown once and stored only as a hash, so if it is
        lost it must be replaced rather than recovered.
      </P>
      <Pre>{`Authorization: Bearer ffk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</Pre>
      <Table
        head={['Property', 'Detail']}
        rows={[
          ['Shape', <C key="a">ffk_{'{live|test}'}_{'{32 characters}'}</C>],
          ['Header', <>Ordinary bearer auth — the <C>ffk_</C> prefix is what selects key authentication over a user token</>],
          ['Scopes', 'Each key is limited to the operations it was granted'],
          ['Network', 'A key may be restricted to a list of CIDR ranges; an empty list means anywhere'],
          ['Environment', <>A <C>test</C> key is distinguishable at a glance in logs and configuration</>],
        ]}
      />
      <Note tone="danger" title="Keys belong on servers">
        An API key in front-end code is a leaked API key, whatever the bundler does to it.
        Anything the browser needs — a signed embed token, for instance — is minted by your
        backend and handed over as a short-lived value.
      </Note>
      <P>
        Interactive sessions use JWTs instead, obtained from <C>/v1/auth/login</C> and
        refreshed at <C>/v1/auth/refresh</C>. Refresh tokens are single-use: presenting one
        twice is treated as theft and invalidates the whole family.
      </P>

      <H2 id="conventions">Conventions across the API</H2>
      <Terms
        items={[
          {
            term: 'Public ids',
            children: <>
              Everything on the wire uses a prefixed public id returned as <C>id</C> —{' '}
              <C>f_</C> a form, <C>co_</C> a company, <C>ws_</C> a workspace,{' '}
              <C>emb_</C> an embed. Database row ids are never exposed, so an id is safe to
              log and to put in a URL.
            </>,
          },
          {
            term: 'Errors',
            children: <>
              RFC 9457 problem details, with a stable machine-readable <C>code</C>, a{' '}
              <C>requestId</C> to quote in support, and for validation failures an{' '}
              <C>errors</C> array naming the offending path. Branch on <C>code</C>, never on
              the human-readable message.
            </>,
          },
          {
            term: 'Paging',
            children: <>
              Form lists page by offset and return{' '}
              <C>{'{ data, page, pageSize, total, totalPages, hasMore }'}</C>. Response
              collections page by cursor, because they are append-only feeds where an insert
              would shift every later row — follow <C>nextCursor</C> rather than counting.
            </>,
          },
          {
            term: 'Idempotency',
            children: <>
              Submitting a response requires an <C>Idempotency-Key</C> header. Send the same
              key when retrying and you get the original result rather than a duplicate.
            </>,
          },
        ]}
      />
      <Pre title="A validation failure">{`HTTP/1.1 422 Unprocessable Content
{
  "type": "https://docs.formforge.io/errors/validation-failed",
  "title": "Validation Failed",
  "status": 422,
  "code": "validation_failed",
  "requestId": "0HNODP4J0UFD3:00000004",
  "errors": [
    { "path": "pages[0].elements[0].type", "code": "QUESTION_TYPE_UNKNOWN",
      "message": "Unknown question type." }
  ]
}`}</Pre>

      <H2 id="endpoints">The endpoints you will use</H2>
      <Table
        head={['Group', 'Covers']}
        rows={[
          [<C key="a">/v1/forms</C>, 'List, create, read and write the definition, validate, publish, close, versions'],
          [<C key="b">/v1/forms/{'{id}'}/responses</C>, 'Responses for one form, and CSV export'],
          [<C key="c">/v1/forms/{'{id}'}/analytics</C>, 'Rollups and summaries'],
          [<C key="d">/v1/forms/{'{id}'}/invites</C>, 'Single-use links'],
          [<C key="e">/v1/forms/{'{id}'}/embeds</C>, 'Embeds; origins and tokens under /v1/embeds'],
          [<C key="f">/v1/projects</C>, 'Folders, and filing a form into one'],
          [<C key="g">/v1/workspaces</C>, 'Workspaces'],
          [<C key="h">/v1/companies</C>, 'Companies, their members and workspaces'],
          [<C key="i">/v1/themes</C>, 'Themes and previews'],
          [<C key="j">/v1/webhooks</C>, 'Subscriptions and delivery history'],
          [<C key="k">/v1/api-keys</C>, 'Machine credentials'],
          [<C key="l">/v1/audit-logs</C>, 'Who changed what'],
          [<C key="m">/v1/public</C>, 'Anonymous: which version of a form is live'],
          [<C key="n">/r/{'{versionKey}'}</C>, 'Anonymous runtime: definition, sessions, responses, files'],
        ]}
      />

      <H3>Creating and publishing a form from code</H3>
      <Pre title="Server-side">{`# 1. Create it — you get a draft version with it
POST /v1/forms
     Authorization: Bearer ffk_live_…
     { "name": "NPS Q3", "type": 1, "workspaceId": "ws_…" }
  -> { "id": "f_…", "draftVersionId": "…" }

# 2. Read the empty definition, change it, write it back
GET  /v1/forms/{formId}/definition
PUT  /v1/forms/{formId}/definition
     { …the whole document… }

# 3. Publish. Errors block; warnings do not.
POST /v1/forms/{formId}/publish
     { "changeSummary": "Initial release" }`}</Pre>
      <Note tone="warn">
        <C>PUT /definition</C> replaces the whole document — read, modify, write. Sending a
        partial document removes what you left out. Types matter too:{' '}
        <C>schemaVersion</C> is a string, and sending a number is rejected with a JSON
        conversion error rather than a helpful field message.
      </Note>

      <H3>Reading responses</H3>
      <Pre>{`GET /v1/forms/{formId}/responses?cursor=…
  -> { "data": [...], "hasMore": true, "nextCursor": "…" }

GET /v1/forms/{formId}/responses.csv        # the same data as a download`}</Pre>
      <P>
        Poll this on a schedule, or subscribe to <C>response.submitted</C> and pull only what
        changed. For anything more than a handful of forms the webhook is the cheaper path.
      </P>

      <H2 id="webhooks">Webhooks</H2>
      <P>
        Subscribe an endpoint of yours to events. Delivery is at-least-once, so your handler
        must be idempotent — key on the delivery id.
      </P>
      <Table
        head={['Event', 'Fires when']}
        rows={[
          [<C key="a">response.submitted</C>, 'A response was accepted'],
          [<C key="b">form.published</C>, 'A new version went live'],
          [<C key="c">form.unpublished</C>, 'A form was taken down'],
          [<C key="d">form.closed</C>, 'A form stopped accepting submissions'],
        ]}
      />
      <H3>Headers on every delivery</H3>
      <Table
        head={['Header', 'Carries']}
        rows={[
          [<C key="a">FormForge-Signature</C>, <>{'t={unix seconds},v1={hex HMAC-SHA256}'}</>],
          [<C key="b">FormForge-Event</C>, 'The event type'],
          [<C key="c">FormForge-Delivery</C>, 'Delivery id — use it to deduplicate'],
          [<C key="d">FormForge-Trace-Id</C>, 'Correlates with the originating request, when present'],
        ]}
      />
      <H3>Verifying the signature</H3>
      <P>
        The signed material is the timestamp, a full stop, then the raw body — so verify
        against the bytes you received, before any JSON parsing or re-serialising.
      </P>
      <Pre title="Node">{`import { createHmac, timingSafeEqual } from 'node:crypto';

function verify(header, rawBody, secret, toleranceSeconds = 300) {
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));
  const age = Math.abs(Date.now() / 1000 - Number(parts.t));
  if (!Number.isFinite(age) || age > toleranceSeconds) return false;   // replay window

  const expected = createHmac('sha256', secret)
    .update(\`\${parts.t}.\${rawBody}\`)
    .digest('hex');

  const a = Buffer.from(expected);
  const b = Buffer.from(parts.v1 ?? '');
  return a.length === b.length && timingSafeEqual(a, b);
}`}</Pre>
      <Note tone="warn">
        The timestamp is inside the signed material precisely so a captured request cannot be
        replayed later. Rejecting anything outside a tolerance window is the point of it —
        checking the signature alone leaves replay open.
      </Note>

      <H2 id="runtime">Driving the runtime yourself</H2>
      <P>
        The embeddable runtime is plain ES2020 with no dependencies. It fetches the published
        definition, evaluates logic exactly as the server does, saves partial progress, and
        reports what is happening over <C>postMessage</C>. Every message carries{' '}
        <C>__ff: 2</C> — the protocol version, which is how you tell FormForge messages from
        everything else on the channel.
      </P>
      <Pre title="Listening without the loader">{`window.addEventListener('message', (ev) => {
  if (ev.origin !== 'https://your-formforge-host') return;   // always check the origin
  const m = ev.data;
  if (!m || m.__ff !== 2) return;

  switch (m.type) {
    case 'ready':    console.log(m.formId, m.versionKey); break;
    case 'resize':   frame.style.height = m.height + 'px'; break;
    case 'complete': onDone(m.responseId); break;
    case 'error':    if (m.retryable) retry(); break;
  }
});

// Liveness check — the runtime answers with { type: 'pong', nonce }
frame.contentWindow.postMessage({ type: 'ping', nonce: 42 }, 'https://your-formforge-host');`}</Pre>
      <Note tone="danger" title="Check the origin">
        Omitting the origin check is the most common vulnerability in embed code — without
        it any page in any frame can post you a message shaped like a completion. The
        supplied loader does this for you.
      </Note>

      <H3>Submitting without the runtime</H3>
      <OL>
        <li><C>GET /v1/public/forms/{'{formId}'}/current</C> — the live <C>versionKey</C>.</li>
        <li><C>GET /r/{'{versionKey}'}/definition</C> — the document to render.</li>
        <li><C>POST /r/{'{versionKey}'}/sessions</C> — open a session.</li>
        <li><C>POST /r/{'{versionKey}'}/responses</C> with an <C>Idempotency-Key</C>.</li>
      </OL>
      <Note tone="warn" title="Two things that fail a first integration">
        A form carries a minimum completion time, measured on the server from the session —
        so opening a session and submitting immediately is rejected as{' '}
        <C>submission_too_fast</C>. And the server re-evaluates every logic rule at submit,
        rejecting answers to questions it believes were hidden: send what a respondent could
        actually have answered.
      </Note>

      <H2 id="definition">The form definition</H2>
      <P>
        One portable JSON document describes a form completely — pages, elements, variables,
        logic, scoring, outcomes and translations. It is the contract between the studio, the
        server and the runtime, and it is what you manipulate when creating forms from code.
      </P>
      <UL>
        <li>Logic is <strong>data, not code</strong>: conditions are objects, so they can be generated, diffed and analysed without executing anything.</li>
        <li>Translations live in a side table keyed by path, so adding them does not change the shape of anything else.</li>
        <li>Variables declare their source, and only <C>token</C> values are trusted — <C>query</C> values are attacker-supplied by definition.</li>
      </UL>

      <H2 id="limits">Rate limits and resilience</H2>
      <UL>
        <li>Ingest endpoints carry request ceilings; a caller over the limit gets 429. Back off and retry rather than looping.</li>
        <li>Webhook delivery retries with backoff. Return 2xx quickly and do the work asynchronously — a slow handler causes retries and duplicates.</li>
        <li>Response limits are read from sharded counters, so they are approximate under concurrency and a burst can overshoot slightly.</li>
      </UL>

      <H2 id="production">Going to production</H2>
      <UL>
        <li>Separate <C>test</C> and <C>live</C> keys, and restrict live keys by CIDR where your egress addresses are stable.</li>
        <li>Verify webhook signatures, including the timestamp window, and deduplicate on the delivery id.</li>
        <li>Store the <C>requestId</C> from failed calls — it is what makes a support conversation short.</li>
        <li>Pin nothing to a form's draft: publish, then reference the published form.</li>
        <li>Handle a closed or unpublished form gracefully; forms outlive the code that embeds them.</li>
      </UL>
    </>
  );
}
